<script setup>
    import CloneFormatModal from '@/apps/ens/components/CloneFormatModal.vue';
    import DisplaySequenceTag from '@/apps/ens/components/DisplaySequenceTag.vue';
    import EnsFormatValidationResult from './EnsFormatValidationResult.vue';
    import EditTableFormatElement from '@/apps/ens/components/EditTableFormatElement.vue';
    import SortFormatsModal from '@/apps/ens/components/SortFormatsModal.vue';
    import StatusIndicator from '@/apps/ens/components/StatusIndicator.vue';
    import { newFormatElement, queries, sortFormatsForDisplay } from '@/apps/ens/helpers.ts';
    import useBuefy from '@/shared/composables/useBuefy.ts';
    import CodeEditSaveProgress from '@/shared/components/CodeEditSaveProgress.vue';
    import MarkdownEditorModal from '@/shared/components/MarkdownEditorModal.vue';
    import VueMarkdown from '@/shared/components/VueMarkdown.vue';
    import CustomSelect from '@/shared/components/CustomSelect.vue';
    import ScopeDropdown from '@/shared/components/ScopeDropdown.vue';
    import useShow from '@/shared/composables/useShow.js';
    import { requireCanEditCode as vRequireCanEditCode } from '@/shared/directives/requirePermission';
    import {
        createReleaseChain,
        ensValidateFormat,
        getAccessGroupForLibrary,
        getCodeSets,
        getLibrary
    } from '@/shared/helpers/api';
    import { assert } from '@/shared/helpers/assert';
    import { InputRegexValidator } from '@/shared/helpers/inputRegexValidator.ts';
    import { spaceEncode } from '@/shared/helpers/spaceEncoding.js';
    import { cancelPreviousWhenCalledWithDebouncing, compareByProperty, equalsIgnoreCase } from '@/shared/helpers/utils';
    import { http } from '@/shared/httpWrapper.js';
    import { useScopeStore } from '@/stores/scopeStore.ts';
    import _ from 'lodash';
    import { computed, ref, toRefs, watchEffect } from 'vue';
    import { useRoute, useRouter } from 'vue-router';

    // =================================================================================================================
    // == Props

    const props = defineProps({
        ensType: {
            type: String,
            required: true
        }
    });
    const { ensType } = toRefs(props);

    // =================================================================================================================
    // == Composables & State

    const route = useRoute();
    const router = useRouter();

    const buefy = useBuefy();

    const { showError, showInfo } = useShow();
    const getErrorMessage = (ex) => {
        return new Promise(resolve => showError(ex, resolve));
    };

    const scopeStore = useScopeStore();

    const initialLoading = ref(true);
    const fatalErrorMessage = ref(null);

    const selectedFacility = ref(null);

    const categories = ref([]);
    const selectedCategory = ref(null);

    const formats = ref([]);
    const loadingFormats = ref(false);
    const loadingFormatElements = ref(false);
    /**
     * Invariants:
     * 1) For each element e: `e.isValid == true`
     * 2) For each element e: `e.elementNo == elements.indexOf(e)`
     */
    const selectedFormat = ref(null);

    const layouts = ref([]);
    const references = ref([]);
    const paddings = ref([]);

    const cloneFormatModalActive = ref(false);
    const sortFormatsModalActive = ref(false);

    const previousValidationRequest = ref(null);
    const previousValidationResult = ref(null);
    const validationResult = ref(null);
    const validationLoading = ref(false);

    const loadingCreateRelease = ref(false);
    const releaseId = ref(null);
    const releaseError = ref(null);

    const accessGroupsForFormatLib = ref([]);

    const formatDescriptionRegexValidator = ref(null);
    const elementDescriptionRegexValidator = ref(null);
    const commentsRegexValidator = ref(null);
    const uiExplanationRegexValidator = ref(null);

    const showUIExplanationModal = ref(false);

    // =================================================================================================================
    // Getters

    const title = computed(() =>
        ensType.value === 'TagFormat'
            ? 'ENS Tag Format'
            : 'ENS Document Numbering Format');

    const ensNumberTypeDescription = computed(() =>
        ensType.value === 'TagFormat'
            ? 'tag number'
            : 'document number');

    const libraryNames = computed(() =>
        ensType.value === 'TagFormat'
            ? ['TagFormat', 'TagFormatElement']
            : ['DocumentNumberingFormat', 'DocumentNumberingFormatElement']);

    const filteredCategories = computed(() =>
        categories.value.filter(x => x.facility === selectedFacility.value)
    );

    const categorySelectOptions = computed(() =>
        filteredCategories.value.map(category => ({
            key: category.identity,
            string: category.description,
            value: category
        }))
    );

    const existingCategoryFormats = computed(() => {
        if (!selectedCategory.value)
            return [];

        return formats.value.filter(format =>
            format.category === selectedCategory.value.identity && format.isValid
        );
    });

    const filteredFormats = computed(() => {
        if (!selectedCategory.value)
            return [];

        const maybeSelectedFormat = selectedFormat.value ? [selectedFormat.value] : [];
        return sortFormatsForDisplay(_.unionBy(maybeSelectedFormat, existingCategoryFormats.value, 'name'));
    });

    const formatSelectOptions = computed(() =>
        filteredFormats.value.map(format => ({
            key: format.identity,
            string: format.description || '(Empty description)',
            value: format
        }))
    );

    const originalFormat = computed(() =>
        formats.value.find(x => x.name === selectedFormat.value?.name) ?? null
    );

    const selectedFormatWithAllChanges = computed(() => {
        let elements = selectedFormat.value.elements;
        if (elements && originalFormat.value?.elements) {
            const removedElements
                = _.difference(
                    originalFormat.value.elements.map(x => x.elementNo),
                    selectedFormat.value.elements.map(x => x.elementNo)
                ).map(elementNo => ({
                    ...newFormatElement(ensType.value, elementNo),
                    description: 'REMOVED',
                    isValid: false
                }));
            elements = elements.concat(removedElements);
        }

        return {
            ...selectedFormat.value,
            syntax: validationResult.value?.value?.syntax ?? null,
            regex: validationResult.value?.value?.regex ?? null,
            regexGrouped: validationResult.value?.value?.regexGrouped ?? null,
            elements: elements
        };
    });

    const hasChangesWithRespectTo = (filter) => {
        if (!selectedFormat.value?.elements)
            return false;
        else if (!originalFormat.value)
            return true;
        else
            return !_.isEqual(filter(selectedFormatWithAllChanges.value), filter(originalFormat.value));
    };

    const filteredFormatForChanges = (format) => {
        return {
            ..._.omit(format, ['identity']),
            elements:
                format.elements?.map(element =>
                    _.omit(element,
                           ['referenceError',
                            'referenceMandatoryError',
                            'referenceRecommendedWarning',
                            'minMaxError',
                            'constantValueError',
                            'layoutError',
                            'descriptionError',
                            'paddingError',
                            'identity'])
                )
        };
    };

    const filteredFormatForUserChanges = (format) => {
        return {
            ..._.omit(format, ['syntax', 'regex', 'regexGrouped', 'identity']),
            elements:
                format.elements
                    ?.filter(element => element.isValid)
                    .map(element =>
                        _.omit(element,
                               ['referenceError',
                                'referenceMandatoryError',
                                'referenceRecommendedWarning',
                                'minMaxError',
                                'constantValueError',
                                'layoutError',
                                'descriptionError',
                                'paddingError',
                                'elementNo',
                                'identity'])
                    )
        };
    };

    const hasChanges = computed(() =>
        !loadingFormatElements.value
        && !loadingFormats.value
        && hasChangesWithRespectTo(filteredFormatForChanges));

    const hasUserChanges = computed(() =>
        !loadingFormatElements.value
        && !loadingFormats.value
        && hasChangesWithRespectTo(filteredFormatForUserChanges));

    const isDescriptionUnique = computed(() => {
        if (!selectedFormat.value)
            return false;
        const normalizedDescription = selectedFormat.value.description?.toLowerCase().trim();

        // unique description for a given category of format
        return !formats.value.some(x =>
            x.category === selectedFormat.value.category
            && x.name !== selectedFormat.value.name
            && x.description?.toLowerCase().trim() === normalizedDescription);
    });

    const hasDescription = computed(() =>
        !!selectedFormat.value?.description?.trim()
    );

    const invalidDescriptionError = computed(() => {
        if (!hasDescription.value) {
            return 'Format description cannot be empty';
        }
        if (!isDescriptionUnique.value) {
            return `A format with the same description already exists in category ${selectedCategory.value.description}`;
        }
        const error = formatDescriptionRegexValidator.value?.validate(selectedFormat.value.description);
        return error?.error || '';
    });

    const canAddNewFormat = computed(() =>
        !!selectedFacility.value && !!selectedCategory.value
    );

    const canSortFormats = computed(() =>
        !loadingFormats.value && existingCategoryFormats.value.length > 0
    );

    const canCloneFormat = computed(() =>
        !!selectedFormat.value?.elements
    );

    const canEditFormat = computed(() =>
        !!selectedFormat.value?.elements
    );

    const canDeleteFormat = computed(() =>
        !!selectedFormat.value?.elements
    );

    const canCreateRelease = computed(() =>
        hasChanges.value
        && validationResult.value?.value?.isValid
        && !invalidDescriptionError.value
        && !selectedFormat.value.elements.some(elm =>
            elm.referenceError
            || elm.referenceMandatoryError
            || elm.minMaxError
            || elm.layoutError
            || elm.descriptionError
            || elm.paddingError
        ));

    const isNewFormat = computed(() =>
        !originalFormat.value
    );

    const selectedFormatPlaceholder = computed(() =>
        !!selectedCategory.value && filteredFormats.value.length === 0 ? 'No formats available' : 'Select format'
    );

    const selectedFormatLabel = computed(() => {
        if (!selectedFormat.value) {
            return null;
        }
        if (!selectedFormat.value.description) {
            return '(Empty description)';
        }
        return selectedFormat.value.description;
    });

    const permissionsObject = computed(() => ({
        libraryName: ensType.value,
        scopes: selectedFacility.value ? [selectedFacility.value] : []
    }));

    const hasMissingRecommendedReferences = computed(() =>
        selectedFormat.value?.elements.some(elm => elm.referenceRecommendedWarning !== '')
    );

    // =================================================================================================================
    // == Data fetching

    const loadDescriptionRegex = async () => {
        const [formatLibrary, formatElementLibrary] = await Promise.all(
            libraryNames.value.map(name => getLibrary(http, name, { includeAttributeDefintions: true }))
        );

        const formatDescriptionRegex = formatLibrary.descriptionRegex;
        const formatDescriptionRegexErrorMessage = formatLibrary.descriptionRegexErrorMessage;
        const elementDescriptionRegex = formatElementLibrary.descriptionRegex;
        const elementDescriptionRegexErrorMessage = formatElementLibrary.descriptionRegexErrorMessage;
        const commentsRegex = formatElementLibrary.attributeDefinitions.find(ad => ad.name === 'Comments').regex;
        const commentsRegexErrorMessage = formatElementLibrary.attributeDefinitions.find(ad => ad.name === 'Comments').regexErrorMessage ?? null;
        const uiExplanationRegex = formatLibrary.attributeDefinitions.find(ad => ad.name === 'UIExplanation').regex;
        const uiExplanationRegexErrorMessage = formatLibrary.attributeDefinitions.find(ad => ad.name === 'UIExplanation').regexErrorMessage ?? null;

        formatDescriptionRegexValidator.value = formatDescriptionRegex ? new InputRegexValidator(new RegExp(formatDescriptionRegex), formatDescriptionRegexErrorMessage) : null;
        elementDescriptionRegexValidator.value = elementDescriptionRegex ? new InputRegexValidator(new RegExp(elementDescriptionRegex), elementDescriptionRegexErrorMessage) : null;
        commentsRegexValidator.value = commentsRegex ? new InputRegexValidator(new RegExp(commentsRegex), commentsRegexErrorMessage) : null;
        uiExplanationRegexValidator.value = uiExplanationRegex ? new InputRegexValidator(new RegExp(uiExplanationRegex), uiExplanationRegexErrorMessage) : null;
    };

    const loadAccessGroupsForFormatLib = async () => {
        accessGroupsForFormatLib.value = await getAccessGroupForLibrary(http, ensType.value);
    };

    const loadCategories = async () => {
        try {
            categories.value = await queries.fetchCategories(ensType.value);
        } catch (ex) {
            showError(ex, message => fatalErrorMessage.value = message);
        }
    };

    const loadFormats = async () => {
        if (!selectedFacility.value)
            return;

        loadingFormats.value = true;
        try {
            const fetchedFormats = await queries.fetchFormats(ensType.value, selectedFacility.value);

            for (const format of fetchedFormats) {
                format.elements = null;

                if (format.isOfficial === null) {
                    format.isOfficial = true; // default to true for old formats where this information is missing
                }
            }

            formats.value = fetchedFormats;
        } catch (ex) {
            showError(ex);
        }
        loadingFormats.value = false;
    };

    const loadLayouts = async () => {
        try {
            layouts.value = await queries.fetchLayouts(ensType.value);
        } catch (ex) {
            showError(ex, message => fatalErrorMessage.value = message);
        }
    };

    const loadReferences = async () => {
        try {
            references.value = await queries.fetchReferences(ensType.value);
        } catch (ex) {
            showError(ex, message => fatalErrorMessage.value = message);
        }
    };

    const loadPaddings = async () => {
        if (ensType.value !== 'TagFormat')
            return;

        try {
            paddings.value = await queries.fetchPaddings(ensType.value);
        } catch (ex) {
            showError(ex, message => fatalErrorMessage.value = message);
        }
    };

    const loadFormatElements = async (formatIdentity) => {
        try {
            const elements = await queries.fetchFormatElements(ensType.value, formatIdentity, selectedFacility.value);

            for (const element of elements) {
                element.elementNo = parseInt(element.name);
                delete element.name;

                // Normalize optional attribute values
                element.comments ??= null;
                element.include ??= false;
                element.show ??= false;
                element.required ??= false;
                element.constantValue = element.constantValue ?? '';
            }

            elements.sort(compareByProperty('elementNo'));

            return elements;
        } catch (ex) {
            showError(ex, message => fatalErrorMessage.value = message);
        }
    };

    // =================================================================================================================
    // == Application logic

    const filteredValidationRequest = (items) => {
        return items && {
            ...items,
            elements: items.elements?.map(element => _.omit(element, ['description']))
        };
    };

    const getValidationResult = async (abortSignal, validationRequest) => {
        try {
            const response = await ensValidateFormat(
                http,
                ensType.value,
                validationRequest,
                { signal: abortSignal }
            );
            return {
                value: response.data,
                error: null
            };
        } catch (error) {
            return {
                value: null,
                error: await getErrorMessage(error)
            };
        }
    };

    const validateFormat = cancelPreviousWhenCalledWithDebouncing(
        async function(abortSignal, readyPromise) {
            function isNullish(item) {
                return item === null || item === undefined;
            }

            // Avoid format validation if there are client-side errors
            const invalidElement = (element) =>
                isNullish(element.minimumCharacters) || isNullish(element.maximumCharacters);
            if (!selectedFormat.value.elements || selectedFormat.value.elements.some(invalidElement)) {
                validationResult.value = null;
                validationLoading.value = false;
                return;
            }

            const validationRequest = {
                category: selectedCategory.value.identity,
                facility: selectedFacility.value,
                name: selectedFormat.value.name,
                elements: selectedFormat.value.elements
                    .filter(e => e.include) // Only include elements that are in the ENS number
                    .map(e => ({
                        name: e.elementNo.toString(),
                        description: e.description,
                        constantValue: e.constantValue,
                        minimumCharacters: e.minimumCharacters,
                        maximumCharacters: e.maximumCharacters,
                        required: e.required,
                        include: true,
                        layout: e.layout,
                        padding: e.padding
                    }))
            };

            if (_.isEqual(
                filteredValidationRequest(validationRequest),
                filteredValidationRequest(previousValidationRequest.value)
            )) {
                validationResult.value = previousValidationResult.value;
                validationLoading.value = false;
                return;
            }

            validationLoading.value = true;
            validationResult.value = null;

            await readyPromise;

            const result = await getValidationResult(abortSignal, validationRequest);

            // It is important to check for cancellation before resuming synchronous execution because the signal may
            // have been aborted between the completion of the async function and the resumption of this continuation.
            if (abortSignal.aborted)
                return;

            validationResult.value = result;
            validationLoading.value = false;

            previousValidationRequest.value = validationRequest;
            previousValidationResult.value = result;
        },
        {
            delay: 1000,
            debounceSettings: { leading: true }
        }
    );

    const changeSelectedFormat = async (newFormat, setElementsNull = false) => {
        if (!newFormat) {
            selectedFormat.value = null;
            return;
        }
        selectedFormat.value = {
            ...newFormat,
            elements: setElementsNull
                ? null
                : newFormat.elements || null
        };

        if (originalFormat.value) {
            if (!originalFormat.value.elements) {
                loadingFormatElements.value = true;
                originalFormat.value.elements = await loadFormatElements(originalFormat.value.identity);
                loadingFormatElements.value = false;
            }
            selectedFormat.value.elements ??= originalFormat.value.elements
                .filter(element => element.isValid)
                .map((element, index) => ({ ...element, elementNo: index }));
        }

        await validateFormat();
    };

    const deselectFormat = async () => {
        await changeSelectedFormat(null);
    };

    const deselectCategory = async () => {
        await deselectFormat();
        selectedCategory.value = null;
    };

    const changeSelectedFacility = async (facility) => {
        selectedFacility.value = facility;
        if (facility !== scopeStore.selectedScope) {
            scopeStore.updateSelectedScope(facility);
        }
        await deselectCategory();
        await loadFormats();
    };

    const changeSelectedCategory = async (category) => {
        selectedCategory.value = category;
        await deselectFormat();
    };

    const onSaveSuccess = async () => {
        showInfo('Changes saved!');
        loadingCreateRelease.value = false;
        loadingFormatElements.value = true;
        await loadFormats();
        if (originalFormat.value) {
            await changeSelectedFormat(originalFormat.value, true);
        } else {
            await deselectFormat();
        }
        loadingFormatElements.value = false;
    };

    const getCodeSetLookup = async (libraryName) => {
        const codeSets = await getCodeSets(http, libraryName);
        return Object.fromEntries(
            codeSets.flatMap(codeSet =>
                codeSet.scopes.map(facility => [facility, codeSet.name])
            )
        );
    };

    const createRelease = async (format, facilities = [selectedFacility.value]) => {
        releaseId.value = null;
        releaseError.value = null;
        loadingCreateRelease.value = true;

        const formatToChangeCode = ensType.value === 'TagFormat'
            ? format => ({
                Name: format.name,
                IsValid: format.isValid,
                Description: format.description,
                TagCategory: format.category,
                Syntax: format.syntax,
                Regex: format.regex,
                RegexGrouped: format.regexGrouped,
                UIExplanation: format.uiExplanation,
                IsOfficial: format.isOfficial
            })
            : format => ({
                Name: format.name,
                IsValid: format.isValid,
                Description: format.description,
                DocCategory: format.category,
                Syntax: format.syntax,
                Regex: format.regex,
                RegexGrouped: format.regexGrouped,
                UIExplanation: format.uiExplanation,
                IsOfficial: format.isOfficial
            });

        const elementToChangeCode = ensType.value === 'TagFormat'
            ? (format, element) => ({
                Name: element.elementNo.toString(),
                IsValid: element.isValid,
                Description: element.description,
                Comments: element.comments ?? '{null}',
                MinimumCharacters: element.minimumCharacters,
                MaximumCharacters: element.maximumCharacters,
                InTagNo: element.include,
                Show: element.show,
                Required: element.required,
                SelectFlag: false, // Deprecated: This has been removed from the UI but is still needed by the backend/library.
                ConstantValue: element.constantValue ? spaceEncode(element.constantValue) : '{null}',
                Layout: element.layout,
                Reference: element.reference ?? '{null}',
                TagFormat: format.name,
                Padding: element.padding ?? '{null}'
            })
            : (format, element) => ({
                Name: element.elementNo.toString(),
                IsValid: element.isValid,
                Description: element.description,
                Comments: element.comments ?? '{null}',
                MinimumCharacters: element.minimumCharacters,
                MaximumCharacters: element.maximumCharacters,
                InDocNo: element.include,
                Show: element.show,
                Required: element.required,
                SelectFlag: false, // Deprecated: This has been removed from the UI but is still needed by the backend/library.
                ConstantValue: element.constantValue ? spaceEncode(element.constantValue) : '{null}',
                Layout: element.layout,
                Reference: element.reference ?? '{null}',
                DocNoFormat: format.name
            });

        const formatCodeSets = await getCodeSetLookup(ensType.value);
        const elementCodeSets = await getCodeSetLookup(ensType.value + 'Element');

        const changeDocuments = facilities.flatMap((facility) => {
            const documents = [
                {
                    codeSetName: formatCodeSets[facility],
                    libraryName: ensType.value,
                    codes: [formatToChangeCode(format)]
                },
                {
                    codeSetName: elementCodeSets[facility],
                    libraryName: ensType.value + 'Element',
                    codes: format.elements.map(element => elementToChangeCode(format, element))
                }
            ];

            // When invalidating, the element codeset must be released before the format codeset.
            if (!format.isValid) {
                documents.reverse();
            }

            return documents;
        });

        try {
            const response = await createReleaseChain(http, changeDocuments);
            releaseId.value = response.data.releaseId;
        } catch (error) {
            releaseError.value = error;
        }
    };

    const createDisplaySequenceRelease = async (changes) => {
        releaseId.value = null;
        releaseError.value = null;
        loadingCreateRelease.value = true;

        try {
            const formatCodeSets = await getCodeSetLookup(ensType.value);
            const response = await createReleaseChain(http, [{
                codeSetName: formatCodeSets[selectedFacility.value],
                libraryName: ensType.value,
                codes: changes.map(change => ({
                    Name: change.name,
                    DisplaySequenceNo: change.displaySequenceNo ?? '{null}'
                }))
            }]);
            releaseId.value = response.data.releaseId;
        } catch (error) {
            releaseError.value = error;
        }
    };

    // =================================================================================================================
    // == Event handlers

    const showConfirmDialog = (params) => {
        buefy.dialog.confirm(params);
    };

    const confirmIfFormatIsDirty = (onConfirm, onCancel = null) => {
        if (hasUserChanges.value) {
            showConfirmDialog({
                message: 'Format has changes. If you continue, changes will be discarded.',
                cancelText: 'Cancel',
                confirmText: 'Continue',
                onConfirm: () => onConfirm(),
                onCancel: () => onCancel?.()
            });
        } else {
            onConfirm();
        }
    };

    const onElementsChanged = async (newElements) => {
        selectedFormat.value.elements = newElements;
        await validateFormat();
    };

    const onSelectFacility = (facility) => {
        if (selectedFacility.value === facility)
            return;
        if (cloneFormatModalActive.value)
            return;

        confirmIfFormatIsDirty(
            () => changeSelectedFacility(facility),
            () => {
                // Revert to previous scope
                scopeStore.updateSelectedScope(selectedFacility.value);
            }
        );
    };

    const onSelectCategory = (category) => {
        confirmIfFormatIsDirty(
            () => changeSelectedCategory(category)
        );
    };

    const onClickDiscardChanges = () => {
        showConfirmDialog({
            message: 'Are you sure you want to discard changes?',
            cancelText: 'No',
            confirmText: 'Yes',
            onConfirm: () => changeSelectedFormat(originalFormat.value, true)
        });
    };

    const onSelectFormat = (format) => {
        confirmIfFormatIsDirty(
            () => changeSelectedFormat(format, true)
        );
    };

    const onClickCreateNewFormat = () => {
        confirmIfFormatIsDirty(() => {
            changeSelectedFormat({
                name: crypto.randomUUID(),
                description: '',
                isValid: true,
                facility: selectedFacility.value,
                category: selectedCategory.value.identity,
                syntax: null,
                regex: null,
                regexGrouped: null,
                displaySequenceNo: null,
                elements: []
            });
        });
    };

    const onClickSortFormats = () => {
        confirmIfFormatIsDirty(() => {
            sortFormatsModalActive.value = true;
        });
    };

    const onCloseSortFormatsModal = () => {
        sortFormatsModalActive.value = false;
    };

    const onClickCloneFormat = () => {
        confirmIfFormatIsDirty(() => {
            cloneFormatModalActive.value = true;
        });
    };

    const onCloseCloneFormatModal = () => {
        cloneFormatModalActive.value = false;
        scopeStore.updateSelectedScope(selectedFacility.value);
    };

    const onSubmitCloneFormat = async (event) => {
        const newFormat = {
            ...selectedFormat.value,
            name: crypto.randomUUID(),
            facility: event.facilities[0],
            category: event.category,
            description: event.description,
            displaySequenceNo: null,
            elements: selectedFormat.value.elements.map(element => {
                const newElement = { ...element };
                delete newElement.identity;
                return newElement;
            })
        };
        delete newFormat.identity;

        if (event.facilities.length === 1) {
            await changeSelectedFacility(newFormat.facility);

            selectedCategory.value = filteredCategories.value.find(x => x.identity === newFormat.category);

            await changeSelectedFormat(newFormat);
            return;
        }

        showConfirmDialog({
            title: 'Clone format to multiple facilities',
            message:
                `This will create a release cloning the format to ${event.facilities.length} facilities:<br>
                 <strong>${event.facilities.toSorted().join(', ')}</strong><br><br>
                 Do you want to continue?`,
            cancelText: 'Cancel',
            confirmText: 'Create release',
            onConfirm: async () => {
                await createRelease(newFormat, event.facilities);
            }
        });
    };

    function onClickEditUIExplanation() {
        showUIExplanationModal.value = true;
    };

    function onDoneUIExplanationEdit(value) {
        showUIExplanationModal.value = false;
        selectedFormat.value.uiExplanation = value;
    }

    function onResetUIExplanation() {
        selectedFormat.value.uiExplanation = originalFormat.value?.uiExplanation ?? null;
    }

    const onClickDeleteFormat = () => {
        showConfirmDialog({
            message: `Are you sure you want to delete ${selectedFormat.value.description}?`,
            onConfirm: async () => {
                if (isNewFormat.value) {
                    await deselectFormat();
                } else {
                    await createRelease({
                        ...originalFormat.value,
                        isValid: false,
                        elements: originalFormat.value.elements.map(element => ({
                            ...element,
                            isValid: false
                        }))
                    });
                }
            }
        });
    };

    function getMissingRecommendedReferences(elements) {
        const missingRecommendedReferences = new Set(
            elements
                .filter(element => element.referenceRecommendedWarning !== '')
                .map(element => {
                    const reference = references.value.find(ref => ref.identity === element.reference);
                    return `${reference.description} (#${reference.name})`;
                })
        );
        return Array.from(missingRecommendedReferences);
    }

    const onClickCreateRelease = async () => {
        if (hasMissingRecommendedReferences.value) {
            showConfirmDialog({
                title: 'Recommended settings alert',
                message:
                    `The 'Required' field is recommended but is not checked for the following references:<br>
                     <strong>${getMissingRecommendedReferences(selectedFormat.value.elements).join('<br>')}</strong><br><br>
                     Would you like to proceed with saving anyway?`,
                cancelText: 'Cancel',
                confirmText: 'Save',
                onConfirm: async () => {
                    await createRelease(selectedFormatWithAllChanges.value);
                }
            });
        } else {
            await createRelease(selectedFormatWithAllChanges.value);
        }
    };

    // =================================================================================================================
    // == URL handling

    const initializeSelectionsFromRoute = async () => {
        try {
            const facility
                = scopeStore.scopes
                    .find(scope => equalsIgnoreCase(scope.identity, route.query.facility))
                    ?.identity
                    ?? null;

            // If the facility can't be found, this will clear the preselected facility
            await changeSelectedFacility(facility);

            if (!facility)
                return;

            // AB#169461: 'formatid' and 'name' are deprecated and will be removed in the future
            const identity = (
                route.query.formatIdentity
                || route.query.formatid
                || route.query.name
            )?.toUpperCase(); // Identity is case-insensitive

            if (!identity)
                return;

            const format = formats.value.find(format => format.identity === identity && format.facility === selectedFacility.value);

            if (!format)
                return;

            const category = filteredCategories.value.find(category => category.identity === format.category);
            assert(category, 'Category not found for format');

            await changeSelectedCategory(category);
            await changeSelectedFormat(format);
        } catch (ex) {
            showError(ex, message => fatalErrorMessage.value = 'Unexpected error while loading selection from URL: ' + message);
        }
    };

    const updateRouteFromSelections = async () => {
        if (initialLoading.value)
            return;
        await router.replace({
            query: {
                facility: selectedFacility.value,
                formatIdentity: selectedFormat.value?.identity
            }
        });
    };

    watchEffect(async () => {
        if (initialLoading.value)
            return;
        await updateRouteFromSelections();
    });

    // =================================================================================================================
    // == Initialization

    (async function init() {
        scopeStore.setScopeType('Facility');

        await Promise.all([
            scopeStore.init(),
            loadAccessGroupsForFormatLib(),
            loadCategories(),
            loadLayouts(),
            loadReferences(),
            loadPaddings(),
            loadDescriptionRegex()
        ]);

        if (route.query.facility !== undefined) {
            await initializeSelectionsFromRoute();
        } else if (scopeStore.selectedScope) {
            await changeSelectedFacility(scopeStore.selectedScope);
        }

        initialLoading.value = false;
    })();
</script>

<template>
    <div>
        <b-loading :model-value="initialLoading" />

        <clone-format-modal
            v-if="cloneFormatModalActive"
            :ens-type="ensType"
            :categories="categories"
            :original-format="selectedFormat"
            :facilities="scopeStore.scopes"
            @close="onCloseCloneFormatModal"
            @submit="onSubmitCloneFormat" />

        <sort-formats-modal
            v-if="sortFormatsModalActive"
            :ens-type="ensType"
            :facility="selectedFacility"
            :category-description="selectedCategory.description"
            :formats="existingCategoryFormats"
            :current-format-name="selectedFormat?.name ?? null"
            @close="onCloseSortFormatsModal"
            @submit="createDisplaySequenceRelease" />

        <code-edit-save-progress
            :save-process-started="loadingCreateRelease"
            :release-id="releaseId"
            :external-error="releaseError"
            :close-enabled="false"
            @success="onSaveSuccess" />

        <h1 class="title">
            {{ title }}
        </h1>

        <b-message
            type="is-danger"
            has-icon
            title="Error"
            :model-value="!!fatalErrorMessage"
            @update:model-value="fatalErrorMessage = null">
            {{ fatalErrorMessage }}
        </b-message>

        <b-field grouped>
            <scope-dropdown
                :scope-type="scopeStore.scopeType"
                @update:selected-scope="onSelectFacility" />
            <b-field>
                <custom-select
                    class="category-select"
                    :model-value="selectedCategory"
                    :options="categorySelectOptions"
                    :disabled="!selectedFacility"
                    placeholder="Select category"
                    :label="selectedCategory?.description"
                    icon="folder"
                    icon-pack="mdi"
                    @update:model-value="onSelectCategory" />
            </b-field>
            <b-field>
                <custom-select
                    class="format-select"
                    :model-value="selectedFormat"
                    :options="formatSelectOptions"
                    :disabled="!selectedCategory || !filteredFormats.length"
                    :placeholder="selectedFormatPlaceholder"
                    :label="selectedFormatLabel"
                    icon="file"
                    icon-pack="mdi"
                    :loading="loadingFormats"
                    @update:model-value="onSelectFormat">
                    <template #label>
                        <display-sequence-tag
                            v-if="selectedFormat"
                            class="mr-2"
                            :value="selectedFormat.displaySequenceNo" />
                        {{ selectedFormatLabel || selectedFormatPlaceholder }}
                    </template>
                    <template #option="{ option }">
                        <display-sequence-tag
                            class="mr-2"
                            :value="option.value.displaySequenceNo" />
                        {{ option.string }}
                    </template>
                </custom-select>
                <p class="control">
                    <b-button
                        v-require-can-edit-code="permissionsObject"
                        icon-left="sort-numeric-ascending"
                        label="Edit order"
                        title="Edit the order of the formats in this category"
                        :disabled="!canSortFormats"
                        @click="onClickSortFormats" />
                </p>
            </b-field>
        </b-field>
        <hr>

        <b-field
            grouped>
            <b-field>
                <p
                    class="control">
                    <b-button
                        v-require-can-edit-code="permissionsObject"
                        icon-left="plus"
                        :disabled="!canAddNewFormat"
                        @click="onClickCreateNewFormat">
                        Add format
                    </b-button>
                </p>
            </b-field>
            <b-field>
                <p
                    class="control">
                    <b-button
                        icon-left="content-copy"
                        :disabled="!canCloneFormat"
                        @click="onClickCloneFormat">
                        Clone format
                    </b-button>
                </p>
            </b-field>
            <b-field>
                <p
                    class="control">
                    <b-button
                        v-require-can-edit-code="permissionsObject"
                        icon-left="comment"
                        :disabled="!canEditFormat"
                        @click="onClickEditUIExplanation">
                        Edit explanation
                    </b-button>
                </p>
            </b-field>
            <b-field>
                <p
                    class="control">
                    <b-button
                        v-require-can-edit-code="permissionsObject"
                        icon-left="trash-can"
                        :disabled="!canDeleteFormat"
                        @click="onClickDeleteFormat">
                        Delete format
                    </b-button>
                </p>
            </b-field>
            <b-field>
                <p
                    v-require-can-edit-code="permissionsObject"
                    class="control pt-2">
                    <b-tooltip
                        label="Only official formats are published for contractors in the Equinor LCI portal."
                        position="is-bottom">
                        <b-checkbox
                            v-if="selectedFormat"
                            v-model="selectedFormat.isOfficial"
                            type="is-light"
                            :disabled="!canEditFormat">
                            Is official
                        </b-checkbox>
                    </b-tooltip>
                </p>
            </b-field>
            <b-field
                class="is-right-justified" />
            <status-indicator
                :has-user-changes="hasUserChanges"
                :validation-loading="validationLoading"
                :has-validation-error="!!validationResult?.error" />
            <b-field>
                <p
                    class="control">
                    <b-button
                        icon-left="undo"
                        :disabled="!hasUserChanges"
                        @click="onClickDiscardChanges">
                        Revert
                    </b-button>
                </p>
            </b-field>
            <b-field>
                <p
                    class="control">
                    <b-button
                        v-require-can-edit-code="permissionsObject"
                        :loading="loadingCreateRelease"
                        :disabled="!canCreateRelease"
                        icon-left="floppy"
                        type="is-primary"
                        @click="onClickCreateRelease">
                        Save
                    </b-button>
                </p>
            </b-field>
        </b-field>

        <div class="columns mb-4">
            <div class="column">
                <b-field
                    v-if="selectedFormat"
                    label="Format Description"
                    :type="invalidDescriptionError ? 'is-danger' : ''"
                    :message="invalidDescriptionError">
                    <b-input
                        v-model="selectedFormat.description"
                        :maxlength="invalidDescriptionError ? formatDescriptionRegexValidator?.maxLength: undefined"
                        expanded />
                </b-field>
            </div>
            <div class="column has-text-centered is-flex is-flex-direction-column is-justify-content-center">
                <ens-format-validation-result
                    v-if="selectedFormat"
                    class="block"
                    :loading="validationLoading"
                    :result="validationResult?.value ?? null"
                    :error="validationResult?.error ?? null" />
            </div>
        </div>

        <edit-table-format-element
            v-if="selectedFormat"
            :ens-type="ensType"
            :loading="loadingFormatElements"
            :initial-elements="selectedFormat.elements ?? []"
            :layouts="layouts"
            :references="references"
            :paddings="paddings"
            :format-name="selectedFormat.name"
            :is-format-new="isNewFormat"
            :description-regex-validator="elementDescriptionRegexValidator"
            :comments-regex-validator="commentsRegexValidator"
            :permissions-object="permissionsObject"
            @change="onElementsChanged">
            <template #bottom-toolbar-content>
                <p class="control">
                    To validate a {{ ensNumberTypeDescription }} using the defined formats, use the
                    <router-link
                        to="/EnsNumber">
                        <span>ENS number validator</span>
                    </router-link>.
                </p>
            </template>
        </edit-table-format-element>
        <p
            v-else
            style="text-align: center"
            class="has-margin-top2">
            No format selected.
        </p>

        <div
            v-if="selectedFormat?.uiExplanation"
            class="content">
            <hr class="mt-0">
            <vue-markdown
                :source="selectedFormat.uiExplanation"
                :html-enabled="false" />
        </div>
    </div>

    <markdown-editor-modal
        v-if="showUIExplanationModal"
        title="Edit UI explanation"
        :value="selectedFormat.uiExplanation ?? ''"
        :regex-validator="uiExplanationRegexValidator"
        @reset="onResetUIExplanation"
        @done="onDoneUIExplanationEdit" />
</template>

<style scoped>
.category-select :deep(button),
.format-select :deep(button) {
    min-width: 200px;
}

/* Attach the Sort button to the format select */
.format-select :deep(button) {
    border-start-end-radius: 0;
    border-end-end-radius: 0;
}

.is-right-justified {
    margin-left: auto;
}
</style>
